import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import {
  Sparkles, Bot, Wrench, DollarSign, ClipboardList, ScanEye,
  User as UserIcon, Mic, FileText, ArrowUpRight,
} from "lucide-react";

/**
 * Every AI feature lives in this hub.  Cards use pastel gradients so the
 * page reads as a friendly "app launcher" for residents — the same
 * dark-mode `bg-card` styling on admin thanks to `theme`.
 */
const AGENTS = [
  {
    slug: "avatar",
    title: "AI Avatar Concierge",
    desc: "Your 24/7 hyper-personalized digital assistant with live voice and video.",
    icon: UserIcon,
    // Pastel teal → light background
    lightBg: "bg-gradient-to-br from-teal-100/70 via-teal-50 to-white border-teal-200",
    darkBg: "border-teal-500/30 bg-gradient-to-br from-teal-500/[0.08] via-transparent to-transparent",
    accent: "text-teal-600",
    darkAccent: "text-teal-300",
  },
  {
    slug: "concierge",
    title: "AI Chat Assistant",
    desc: "Ask anything about the community, amenities, or rules via text chat. Available 24/7.",
    icon: Bot,
    lightBg: "bg-gradient-to-br from-indigo-100/70 via-indigo-50 to-white border-indigo-200",
    darkBg: "border-indigo-500/30 bg-gradient-to-br from-indigo-500/[0.08] via-transparent to-transparent",
    accent: "text-indigo-600",
    darkAccent: "text-indigo-300",
  },
  {
    slug: "voice",
    title: "Voice AI Concierge",
    desc: "Call your AI Assistant to book amenities or log maintenance issues conversationally.",
    icon: Mic,
    lightBg: "bg-gradient-to-br from-rose-100/70 via-rose-50 to-white border-rose-200",
    darkBg: "border-rose-500/30 bg-gradient-to-br from-rose-500/[0.08] via-transparent to-transparent",
    accent: "text-rose-600",
    darkAccent: "text-rose-300",
  },
  {
    slug: "maintenance",
    title: "Maintenance AI",
    desc: "Diagnose home issues, get DIY fixes, and generate detailed work orders.",
    icon: Wrench,
    lightBg: "bg-gradient-to-br from-orange-100/70 via-orange-50 to-white border-orange-200",
    darkBg: "border-orange-500/30 bg-gradient-to-br from-orange-500/[0.08] via-transparent to-transparent",
    accent: "text-orange-600",
    darkAccent: "text-orange-300",
  },
  {
    slug: "payment-advisor",
    title: "Payment Advisor",
    desc: "Analyze your ledger, predict future dues, and optimize your payments.",
    icon: DollarSign,
    lightBg: "bg-gradient-to-br from-zinc-100 via-zinc-50 to-white border-zinc-200",
    darkBg: "border-zinc-500/30 bg-gradient-to-br from-zinc-500/[0.08] via-transparent to-transparent",
    accent: "text-zinc-700",
    darkAccent: "text-zinc-300",
  },
  {
    slug: "arc",
    title: "ARC Request AI",
    desc: "Auto-fill architectural applications and check compliance instantly.",
    icon: ClipboardList,
    lightBg: "bg-gradient-to-br from-blue-100/70 via-blue-50 to-white border-blue-200",
    darkBg: "border-blue-500/30 bg-gradient-to-br from-blue-500/[0.08] via-transparent to-transparent",
    accent: "text-blue-600",
    darkAccent: "text-blue-300",
  },
  {
    slug: "violation",
    title: "Violation Pre-Check",
    desc: "Scan your property with AI to catch potential violations before the inspector does.",
    icon: ScanEye,
    lightBg: "bg-gradient-to-br from-purple-100/70 via-purple-50 to-white border-purple-200",
    darkBg: "border-purple-500/30 bg-gradient-to-br from-purple-500/[0.08] via-transparent to-transparent",
    accent: "text-purple-600",
    darkAccent: "text-purple-300",
  },
  {
    slug: "documents",
    title: "CC&R / Document Assistant",
    desc: "Ask questions about the community rules, guidelines, and policies and get instant answers from the CC&Rs.",
    icon: FileText,
    lightBg: "bg-gradient-to-br from-emerald-100/70 via-emerald-50 to-white border-emerald-200",
    darkBg: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] via-transparent to-transparent",
    accent: "text-emerald-600",
    darkAccent: "text-emerald-300",
  },
];

export default function AIHubPage() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const basePath = isAdmin ? "/admin/ai" : "/resident/ai";

  return (
    <div>
      <PageHeader
        eyebrow="AI · Claude Sonnet 4.5"
        title={isAdmin ? "AI Command Center" : "Your AI Concierge Suite"}
        subtitle={
          isAdmin
            ? "Every assistant available to your residents — plus operator-side tools."
            : "Eight assistants that know your community.  Pick one, or just talk."
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {AGENTS.map((a) => {
          const I = a.icon;
          return (
            <Link
              key={a.slug}
              to={`${basePath}/${a.slug}`}
              className="block group"
              data-testid={`ai-hub-${a.slug}`}
            >
              <Card
                className={
                  "p-6 border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg h-full flex flex-col " +
                  (isAdmin ? a.darkBg + " text-white" : a.lightBg)
                }
              >
                <div className="flex items-start justify-between">
                  <div
                    className={
                      "h-11 w-11 rounded-lg flex items-center justify-center " +
                      (isAdmin ? "bg-white/[0.04] border border-white/10" : "bg-white shadow-sm border border-zinc-200")
                    }
                  >
                    <I className={"h-5 w-5 " + (isAdmin ? a.darkAccent : a.accent)} />
                  </div>
                  <ArrowUpRight
                    className={
                      "h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity " +
                      (isAdmin ? "text-zinc-400" : "text-zinc-500")
                    }
                  />
                </div>
                <div className={"mt-5 text-lg font-semibold font-display " + (isAdmin ? "text-white" : "text-zinc-900")}>
                  {a.title}
                </div>
                <div className={"text-sm mt-1.5 leading-relaxed " + (isAdmin ? "text-zinc-400" : "text-zinc-600")}>
                  {a.desc}
                </div>
                {a.slug === "documents" && !isAdmin && (
                  <div className={"mt-3 inline-flex items-center gap-1 text-[11px] " + a.accent}>
                    <Sparkles className="h-3 w-3" /> Cites your community&apos;s CC&amp;Rs
                  </div>
                )}
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
